import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import LoadingScreen from '@/components/LoadingScreen';
import { useAccessToken } from '@/components/session/AccessTokenContext';
import { PATHS } from '@/routes/paths';
import { setCookie } from '@/utils/setCookie';

// Entry point for the Storybook auth flow on staging.
//
// Flow for an unauthenticated user visiting /storybook/index.html:
//   1. Lambda@Edge (viewer-request) sees no valid cookie → redirects to /storybook
//   2. CloudFront has no S3 key for /storybook → falls back to the React SPA
//   3. AuthenticationGuard enforces Auth0 login (returnTo: /storybook)
//   4. After login this component mounts, waits for the access token, writes it
//      into the predium_storybook_auth cookie, then navigates to /storybook/index.html
//   5. Lambda@Edge now sees a valid cookie and lets the S3 request through
//
// For a returning authenticated user the cookie refresh (steps 1–5) is silent —
// no login prompt, just a brief loading state.
export function StorybookRedirect() {
  const isStaging = import.meta.env.VITE_ENV === 'staging';
  const { accessToken } = useAccessToken();

  useEffect(() => {
    if (!isStaging || !accessToken) return;

    let expiry: Date;
    try {
      const base64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded));
      expiry = payload.exp ? new Date(payload.exp * 1000) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    } catch {
      expiry = new Date(Date.now() + 24 * 60 * 60 * 1000);
    }

    setCookie('predium_storybook_auth', accessToken, {
      secure: true,
      domain: window.location.hostname,
      path: '/storybook',
      samesite: 'lax',
      expires: expiry,
    });

    window.location.replace('/storybook/index.html');
  }, [isStaging, accessToken]);

  if (!isStaging) {
    return <Navigate to={PATHS.notFound()} replace />;
  }

  return <LoadingScreen />;
}
